/**
 * Videocall Modal Helpers
 *
 * Modal event wiring, recording button, mute/unmute, and action button toggles.
 */
const $ = jQuery;
import { getAdjacentVoiceIndex } from './videocallVoice';
import { hangupVideocall, toggleVideocallMute } from './videocallActions';
import { setupVideocallCamera } from './videocallCamera';

/**
 * Wire up all videocall modal events (close, hangup, match-voice, mute).
 *
 * @param {jQuery} modal
 * @param {object} ctx
 * @param {boolean} ctx.isModalOpen          Mutable state
 * @param {number}  ctx.dotsInterval         Mutable state
 * @param {number}  ctx.timeoutId            Mutable state
 * @param {string}  ctx.startRecordingBtnText
 * @param {boolean} ctx.isMuted              Mutable state
 * @param {boolean} ctx.btnRecordingTriggered Mutable state
 * @param {number}  ctx.reconnectAttempts    Mutable state
 * @param {number}  ctx.maxReconnectAttempts
 * @param {function} ctx.stopAudio
 * @param {function} ctx.stopLoadingAnimation
 * @param {function} ctx.stopRecording
 * @param {function} ctx.hideActionBtns
 * @param {function} ctx.abortAjax
 * @param {function} ctx.closeSocket
 * @param {function} ctx.syncMatchVoice
 * @param {function} ctx.selectVoice
 * @param {function} ctx.changeVoice
 * @param {function} ctx.muteRecording
 * @param {function} ctx.unMuteRecording
 * @param {function} ctx.initRecordingBtn
 * @param {function} ctx.resetVoiceChangeState
 * @param {function} ctx.debugLog
 */
export function setupModalEvents(modal, ctx) {
    const camera = setupVideocallCamera(modal, {
        startCall: function () {
            const startButton = modal.find('.btn-record:visible').first();
            if (!startButton.length || startButton.prop('disabled')) return false;
            startButton.trigger('click');
            return true;
        },
    });

    modal.off('show.bs.modal.videocallCamera').on('show.bs.modal.videocallCamera', function () {
        camera.stop();
    });

    modal.off('hide.bs.modal.videocall').on('hide.bs.modal.videocall', function () {
        if (ctx.releaseReservationIfUnstarted) ctx.releaseReservationIfUnstarted();
        if (ctx.resetVoiceChangeState) ctx.resetVoiceChangeState();
        ctx.isModalOpen = false;
        clearInterval(ctx.dotsInterval);
        clearTimeout(ctx.timeoutId);
        modal.find('.is-active').removeClass('is-active');
        modal.find('.modal-videocall-actions-action').removeClass('anim-shake');
        $('.g-chat-btn-videocall').removeClass('is-loading');

        $('.modal-videocall .btn-record')
            .removeClass('is-loading')
            .removeClass('is-recording')
            .text(ctx.startRecordingBtnText);

        ctx.stopAudio();
        ctx.stopLoadingAnimation();
        ctx.stopRecording();
        camera.stop();
        ctx.hideActionBtns();
        ctx.reconnectAttempts = 0;
    });

    modal.find('.modal-videocall-actions-hangup').off('click.videocall').on('click.videocall', function () {
        if (ctx.releaseReservationIfUnstarted) ctx.releaseReservationIfUnstarted();
        camera.stop();
        hangupVideocall({
            setModalOpen: function(value) { ctx.isModalOpen = value; },
            setMuted: function(value) { ctx.isMuted = value; },
            setRecordingTriggered: function(value) { ctx.btnRecordingTriggered = value; },
            setReconnectAttempts: function(value) { ctx.reconnectAttempts = value; },
            maxReconnectAttempts: ctx.maxReconnectAttempts,
            stopAudio: ctx.stopAudio,
            stopRecording: ctx.stopRecording,
            closeSocket: ctx.closeSocket,
            abortAjax: ctx.abortAjax,
            hideModal: function() { modal.modal('hide'); },
            clearPresentation: function() {
                modal.find('.is-active').removeClass('is-active');
                modal.find('.modal-videocall-actions-action').removeClass('anim-shake');
            },
        });
    });

    modal.find('.btn-match-voice').off('click.videocall').on('click.videocall', function () {
        const btn = $(this);
        ctx.selectVoice(btn.attr('data-voice-hume'), btn.attr('data-voice-cartesia'), btn);
        setTimeout(() => ctx.syncMatchVoice(modal), 2100);
    });

    modal.find('.modal-videocall-voice-select')
        .off('change.videocall')
        .on('change.videocall', function () {
            ctx.changeVoice($(this));
        });

    modal.find('.modal-videocall-voice-arrow')
        .off('click.videocall')
        .on('click.videocall', function () {
            const select = $(this).closest('.modal-videocall-voice-control').find('.modal-videocall-voice-select');
            if (select.prop('disabled')) return;
            const options = select.find('option');
            const currentIndex = Math.max(0, select.prop('selectedIndex'));
            const direction = $(this).hasClass('is-previous') ? -1 : 1;
            const nextIndex = getAdjacentVoiceIndex(currentIndex, options.length, direction);

            if (nextIndex >= 0) select.prop('selectedIndex', nextIndex).trigger('change');
        });

    modal.find('.modal-videocall-actions-mute')
        .off('click.videocall')
        .on('click.videocall', function () {
            toggleVideocallMute($(this).hasClass('is-active'), {
                mute: ctx.muteRecording,
                unmute: ctx.unMuteRecording,
            });
        });

    const avatarSetting = modal.find('.modal-videocall-avatar-setting');
    const avatarInput = avatarSetting.find('input[name="growtype_chat_live_avatar_enabled"]');

    avatarSetting
        .off('click.videocallAvatar')
        .on('click.videocallAvatar', function(event) {
            if (avatarSetting.attr('data-available') === '1') return;
            event.preventDefault();
            event.stopPropagation();
            avatarInput.prop('checked', false);
            const message = avatarSetting.attr('data-unavailable-message') ||
                'Real-time avatar is temporarily unavailable. You can continue with a voice-only call.';
            ctx.showError(message);
        });

    avatarSetting
        .off('keydown.videocallAvatar')
        .on('keydown.videocallAvatar', function(event) {
            if (avatarSetting.attr('data-available') === '1' || !['Enter', ' '].includes(event.key)) return;
            event.preventDefault();
            avatarSetting.trigger('click');
        });

    avatarInput
        .off('change.videocallAvatar')
        .on('change.videocallAvatar', function() {
            if (avatarSetting.attr('data-available') !== '1') {
                avatarInput.prop('checked', false);
                return;
            }
        });

    ctx.initRecordingBtn();
}

/**
 * Initialize the recording button's click handler.
 *
 * @param {object} ctx
 * @param {string} ctx.startRecordingBtnText
 * @param {boolean} ctx.btnRecordTriggered     Mutable state
 * @param {boolean} ctx.isMuted
 * @param {boolean} ctx.isRecording
 * @param {boolean} ctx.btnRecordingTriggered  Mutable state
 * @param {function} ctx.stopRecording
 * @param {function} ctx.startWebservice
 * @param {function} ctx.startRecording
 * @param {function} ctx.initMic
 * @param {function} ctx.hasAccessToken
 * @param {function} ctx.getSocketState        Returns whether socket is connected
 * @param {function} ctx.debugLog
 */
export function initRecordingBtn(ctx) {
    const selector = '.modal-videocall .btn-record';
    const eventName = 'click.growtypeChatVideocallRecord';
    const $btn = $(selector);

    if (!$btn.text() || $btn.text().trim() === '') {
        $btn.text(ctx.startRecordingBtnText);
    }

    // The modal is replaced with fresh AJAX markup whenever a call opens.
    // Delegate from document so that replacement cannot leave a visible Stop
    // control without a listener.
    $(document).off(eventName, selector).on(eventName, selector, function (event) {
        event.preventDefault();
        const modal = $(this).closest('.modal-videocall');
        const conversationActive = modal.attr('data-conversation-active') === 'true';

        ctx.debugLog("Recording button clicked", {
            btnRecordTriggered: ctx.btnRecordTriggered,
            isMuted: ctx.isMuted,
            isRecording: ctx.isRecording,
            conversationActive: conversationActive,
            socketOpen: ctx.getSocketState(),
        });

        // Stop is a user-level conversation action, so it must remain
        // responsive while an opening response is playing and during the
        // short start debounce window. Recording state only describes the
        // current utterance and can be false while the conversation is live.
        if (conversationActive) {
            // Release the start guard as soon as the user explicitly stops so
            // Continue Conversation works immediately, without a dead tap.
            ctx.btnRecordTriggered = false;
            ctx.stopRecording(false);
            return;
        }

        if (ctx.btnRecordTriggered) return;

        ctx.btnRecordTriggered = true;
        setTimeout(() => { ctx.btnRecordTriggered = false; }, 2000);

        if (ctx.isMuted) return;

        // Restored/hash-opened modals intentionally have no paid reservation
        // yet. Reserve on Start, before asking for microphone access.
        if (!ctx.hasAccessToken()) {
            modal.find('input[name="growtype_chat_live_avatar_enabled"]').prop('disabled', true);
            ctx.startWebservice();
            return;
        }

        ctx.initMic();

        if (!ctx.getSocketState()) ctx.startWebservice();
        if (!ctx.isRecording && !ctx.isMuted) {
            ctx.btnRecordingTriggered = true;
            ctx.startRecording();
        }
    });

    ctx.debugLog("🎤 Recording btn initialized");
}

/**
 * Show the record action button.
 */
export function showActionBtns() {
    $('.modal-videocall .btn-record').fadeIn();
}

/**
 * Hide the record action button.
 */
export function hideActionBtns() {
    $('.modal-videocall .btn-record').fadeOut();
}

/**
 * Mute the microphone and stop recording.
 *
 * @param {object} ctx
 * @param {boolean} ctx.isMuted              Mutable state
 * @param {string}  ctx.startRecordingBtnText
 * @param {function} ctx.stopRecording
 * @param {function} ctx.hideActionBtns
 */
export function muteRecording(ctx) {
    ctx.isMuted = true;
    const muteButton = $('#growtypeChatVideoCallModal .modal-videocall-actions-mute');
    muteButton.addClass('is-active');
    muteButton.closest('.modal-videocall-actions-action').addClass('anim-shake');
    ctx.stopRecording(true);
    ctx.hideActionBtns();
    $('.modal-videocall .btn-record')
        .removeClass('is-loading')
        .removeClass('is-recording')
        .text(ctx.startRecordingBtnText);
}

/**
 * Unmute the microphone and optionally start recording.
 *
 * @param {object} ctx
 * @param {boolean} ctx.isMuted              Mutable state
 * @param {boolean} ctx.isRecording
 * @param {function} ctx.supportsLiveSpeech
 * @param {function} ctx.getSocketState
 * @param {function} ctx.startWebservice
 * @param {function} ctx.startRecording
 * @param {function} ctx.showActionBtns
 */
export function unMuteRecording(ctx) {
    ctx.isMuted = false;
    const muteButton = $('#growtypeChatVideoCallModal .modal-videocall-actions-mute');
    muteButton.removeClass('is-active');
    muteButton.closest('.modal-videocall-actions-action').removeClass('anim-shake');

    if (ctx.supportsLiveSpeech()) {
        if (!ctx.getSocketState()) ctx.startWebservice();
        if (!ctx.isRecording && !ctx.isMuted) ctx.startRecording();
    }

    ctx.showActionBtns();
}


// ── Entry-point Handlers ───────────────────────────────────────────

/**
 * Handle the videocall button click — load modal, fetch session, show UI.
 *
 * @param {jQuery}   submitBtn
 * @param {object}   ctx
 * @param {boolean}  ctx.conversationStopped    Mutable
 * @param {jQuery}   ctx.chatForm               Mutable
 * @param {string}   ctx.sessionToken           Mutable
 * @param {function} ctx.debugLog
 * @param {function} ctx.showError
 * @param {function} ctx.setupModalEvents
 * @param {function} ctx.syncMatchVoice
 * @param {jQuery}   ctx.infoTextContainer      Mutable
 * @param {boolean}  ctx.isModalOpen            Mutable
 * @param {function} ctx.setupBackgroundVideo
 * @param {function} ctx.startLoadingAnimation
 * @param {function} ctx.postToAjax
 * @param {function} ctx.handleAjaxError
 */
export async function handleVideocallClick(submitBtn, ctx, options = {}) {
    if (submitBtn.hasClass('is-loading')) {
        ctx.debugLog("Click ignored - already loading");
        return;
    }

    ctx.conversationStopped = false;
    ctx.chatForm = submitBtn.closest('.g-chatform');

    if (!ctx.chatForm.length) {
        ctx.chatForm = $('.g-chatform').first();
        ctx.debugLog("Button clicked outside .g-chatform, found global form:", ctx.chatForm.attr('id'));
    }

    ctx.sessionToken = ctx.chatForm.attr('data-session-token');
    ctx.debugLog("Videocall clicked", {
        hasSessionToken: !!ctx.sessionToken,
        chatFormId: ctx.chatForm.attr('id'),
    });

    if (!ctx.sessionToken) {
        ctx.debugLog("Session token not found. Returning.");
        ctx.showError('Session token not found. Please refresh the page and try again.');
        return;
    }

    submitBtn.addClass('is-loading');

    let modal = $('#growtypeChatVideoCallModal');
    ctx.debugLog("Preparing videocall modal", { modalAlreadyLoaded: !!modal.length });

    try {
        const response = await $.ajax({
            url: growtype_chat_ajax.url,
            type: 'post',
            dataType: 'json',
            data: {
                action: growtype_chat_ajax.get_videocall_modal_action,
                session_token: ctx.sessionToken,
                nonce: growtype_chat_ajax.nonce,
            }
        });

        if (response.success && response.data.html) {
            if (modal.length) {
                modal.replaceWith(response.data.html);
            } else {
                $('body').append(response.data.html);
            }

            modal = $('#growtypeChatVideoCallModal');
            ctx.debugLog("Videocall modal loaded", { modalFound: !!modal.length });
        } else {
            submitBtn.removeClass('is-loading');
            ctx.showError(response.data?.message || 'Failed to load video call interface.');
            return;
        }
    } catch (err) {
        submitBtn.removeClass('is-loading');
        ctx.handleAjaxError(err, submitBtn);
        return;
    }

    ctx.setupModalEvents(modal);
    ctx.syncMatchVoice(modal);
    ctx.infoTextContainer = modal.find('.e-text[data-type="info"]');
    ctx.setVoiceExperienceState('idle');

    setTimeout(() => {
        if (typeof modal.modal === 'function') {
            modal.modal('show');
            submitBtn.removeClass('is-loading');
        } else {
            submitBtn.removeClass('is-loading');
            modal.addClass('show').css({
                'display': 'block', 'padding-right': '15px', 'background': 'rgba(0,0,0,0.5)',
                'z-index': '10000', 'position': 'fixed', 'top': '0', 'left': '0', 'width': '100%', 'height': '100%'
            });
        }
    }, 100);

    ctx.isModalOpen = true;
    ctx.setupBackgroundVideo(modal);

    ctx.infoTextContainer
        .text('Review the call price, then tap Start Call.')
        .addClass('is-active');
    modal.find('.btn-record').stop(true, true).show().text('Start Call');
    ctx.debugLog("Videocall modal shown; waiting for explicit paid start");
}

/**
 * Handle visibility change — mute when tab is hidden.
 *
 * @param {object} ctx
 * @param {boolean} ctx.isModalOpen
 * @param {function} ctx.stopAudio
 * @param {function} ctx.muteRecording
 */
export function handleVisibilityChange(ctx) {
    if (!ctx.isModalOpen) return;

    if (document.hidden) {
        ctx.stopAudio();
        ctx.muteRecording();
    }
}
